<template>
    <div class="progress-bar-container">
        <div v-if="progress !== null"
            :class="[
                'progress-bar',
                { 'banner': isBannerProgressBar },
                { 'course-list-item': isCourseListItem },
                { 'is-not-started': isItemNotStarted }
            ]"
        >
            <span
                v-if="isSubscribed || isLearningPathItem"
                :class="[
                    'progress-bar',
                    { 'progress-bar__indicator': !isItemComplete },
                    { 'progress-bar__indicator-complete': isItemComplete },
                    { 'progress-bar__indicator-banner': isBannerProgressBar },
                    { 'course-list-item': isCourseListItem }
                ]"
                :style="{ width: `${getProgress}%` }"
            >
            </span>
        </div>
        <div :class="['progress-bar__progress-text', { 'banner': isBannerProgressBar }]">
            <div>
                <number
                    v-if="showAnimatedProgressNumber"
                    ref="animatedProgress"
                    :from="0"
                    :to="getProgress"
                    :duration="2"
                    :delay="0"
                    easing="Power1.easeOut"
                />
                <span v-if="completedDate" class="icon-checked"></span>
                <span class="progress-bar__text" v-html="getProgressText"></span>
            </div>
            <div class="progress-bar__completion-hint" v-if="showCompletionHint">
                <span class="icon-notification" />
                <span v-html="$t('LearningPath.completion_hint')"></span>
            </div>
        </div>
    </div>
</template>

<script>
    import { formattedDateForCountry } from 'components/shared/mixins/dateHelper'
    export default {
        name: 'learning-path-progress-bar',
        props: {
            progress: {
                type: Number,
                default: null
            },
            isCompleted: {
                type: Boolean,
                default: false
            },
            isSubscribed: {
                type: Boolean,
                default: false
            },
            isLearningPathItem: {
                type: Boolean,
                default: false
            },
            isBannerProgressBar: {
                type: Boolean,
                default: false
            },
            isLearningPathProgress: {
                type: Boolean,
                default: false
            },
            isCourseListItem: {
                type: Boolean,
                default: false
            },
            completedDate: {
                type: String,
                default: null
            }
        },
        computed: {
            isItemComplete () {
                return this.isCompleted
            },
            isItemNotStarted () {
                return ((this.isLearningPathProgress && !this.isSubscribed) || (this.isLearningPathItem && this.progress === 0))
            },
            getProgressText () {
                if (this.completedDate !== null) {
                    return `${this.$t('LearningPath.completed_date')}: ${this.formattedDateForCountry(this.completedDate)}`
                }
                if (this.isItemNotStarted && !this.isItemComplete) {
                    return this.$t('LearningPath.not_started')
                }
                if (this.isItemComplete) {
                    return this.$t('LearningPath.completed')
                }
                return `% ${this.$t('LearningPath.percentage_completed')}`
            },
            showAnimatedProgressNumber () {
                if (this.isItemComplete) {
                    return false
                }
                return this.showLearningPathItemProgressNumber || this.showLearningPathBannerProgressNumber
            },
            showLearningPathBannerProgressNumber () {
                return this.isLearningPathProgress && this.isSubscribed && this.progress >= 0
            },
            showLearningPathItemProgressNumber () {
                return this.isLearningPathItem && this.progress > 0
            },
            showCompletionHint () {
                return !this.isBannerProgressBar && !this.isCourseListItem && !this.isItemComplete && 99 <= this.progress && this.progress < 100
            },
            getProgress () {
                if (!this.isItemComplete && this.progress > 100) {
                    return 99
                }
                if ((this.isItemComplete) || (this.progress > 100)) {
                    return 100
                }
                return this.progress
            }
        },
        methods: {
            formattedDateForCountry,
            playAnimation () {
                this.scaleClass = false
                this.$refs.animatedProgress.play()
            }
        }
    }
</script>

<style lang="scss" scoped>
    .progress-bar-container {
        display: flex;
        flex-direction: column;
    }

    .progress-bar {
        width: 100%;
        height: 8px;
        background: $bright_gray;
        display: flex;
        line-height: 30px;
        border-radius: 40px;
        flex-direction: column;
        position: relative;

        &.banner {
            height: 10px;
        }

        &.is-not-started {
            background: $silver_chalice;
        }

        &.course-list-item {
            margin-right: 5px;
        }

        [class*="mobile"] & {
            font-size: 1rem;
        }

        &__indicator {
            position: absolute;
            height: 8px;
        }

        &__indicator:after {
            content: '';
            position: absolute;
            left: 0;
            width: 0;
            height: 100%;
            animation: load 2s;
            animation-timing-function: ease-out;
            animation-fill-mode: both;
            border-radius: 40px;
        }

        &__indicator-banner {
            height: 10px;
        }

        &__indicator-complete {
            background: $green;
        }

        @keyframes load {
            0% {
                width: 0;
                background: $amber;
            }
            100% {
                width: 100%;
                background: $amber;
            }
        }

        &__progress {
            margin: 0 auto;
            z-index: 1;
            font-family: PlusJakartaSans-Regular, sans-serif;
            display: flex;
            align-items: center;
        }

        &__progress-text {
            color: $emperor;
            font-size: 1.4rem;
            font-family: PlusJakartaSans-Regular, sans-serif;
            margin: 5px 0 0;
            white-space: nowrap;
            display: flex;
            align-items: center;
            gap: 10px;

            [class*="mobile"] & {
                font-size: 1.3rem;
            }

            &.banner {
                font-size: 1.6rem;
                display: flex;
                margin: 10px 0 0;
            }
        }

        &__completion-hint {
            color: $chestnut;
            display: flex;
            align-items: center;
            gap: 5px;
        }

        .icon-checked {
            font-size: 1.8rem;
            margin: 0 5px;
            [class*="mobile"] & {
                font-size: 1.4rem;
            }
        }
    }
</style>
